/**
 * ScanPassesPage.js
 *
 * /fly/scan-passes — the first screen guests see when they arrive: a laptop at the door with a USB/Bluetooth 2D
 * scanner. Idle, it's an arrivals gate at dusk: a lit runway rushing towards you, and a phone with a boarding pass
 * under a sweeping laser, inviting guests to scan. When a pass is scanned, the guest is marked as landed and the
 * screen celebrates: it floods with their colour, a plane touches down, "Welcome, <first name>!" rises in, and their
 * boarding pass slides up (MAD ✈ BAH, from <city>, <country>) to be stamped LANDED, with confetti and an airport
 * chime. Guests who already landed get a "welcome back"; an unknown pass, a gentle no.
 * Asks for the staff key once (or takes it from ?key= in the link) and remembers it on this device.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Maximize } from "lucide-react";
import KeyGate from "./components/KeyGate";
import { PLANE_PATH, useWakeLock } from "./ArrivalsPage";
import { getCounterGuests, markArrived } from "./helpers/flyApi";
import { formatTime, useBarcodeScanner, useNoIndex, useStoredKey } from "./helpers/flyPage";

const NIGHT = "#070D1C";
const INK = "#1B2A41";
const PAPER = "#FBF7EF";
const GOLD = "#F2C230";
const RED = "#FF5A4E";
const MUTED = "#5B6577";

// How long each moment stays up, and the least time a welcome is shown before the next guest's
const WELCOME_MS = 8500;
const NOTICE_MS = 4500;
const MIN_WELCOME_MS = 3000;
const SAME_CODE_MS = 6000;
const CONFETTI_PIECES = 52;

const TICKER = "WELCOME TO BAHUBALI ENCLAVE · FLIGHT YJ030 HAS ARRIVED · PLEASE HAVE YOUR BOARDING PASS READY · DRINK. EAT. FLY. · ";

const STYLES = `
.yj-scan{position:fixed;inset:0;overflow:hidden;color:${PAPER};font-family:'Space Grotesk',system-ui,sans-serif;user-select:none;cursor:default;
  background:linear-gradient(180deg,${NIGHT} 0%,#0E1A36 38%,#26244C 56%,#5A3350 64%,#B8613C 70%,#0B0F18 70.2%,#06080D 100%)}
.yj-scan *{box-sizing:border-box}
.yj-scan .mono{font-family:'JetBrains Mono',monospace}
.yj-scan .flap{font-family:'Oswald','Arial Narrow',sans-serif}

/* The sky: stars, a glow on the horizon, a plane crossing now and then */
.yj-scan .star{position:absolute;width:2px;height:2px;border-radius:50%;background:#fff;animation:scan-twinkle var(--t) ease-in-out var(--d) infinite}
@keyframes scan-twinkle{0%,100%{opacity:.15}50%{opacity:.9}}
.yj-scan .glow{position:absolute;left:-10%;right:-10%;top:58%;height:24%;background:radial-gradient(50% 60% at 50% 50%,rgba(242,140,70,.55),transparent 70%);filter:blur(8px)}
.yj-scan .sky-plane{position:absolute;top:12%;left:-10vw;width:min(4.5vw,52px);opacity:.6;animation:scan-cross 26s linear 4s infinite}
.yj-scan .sky-plane::before{content:"";position:absolute;right:62%;top:50%;width:30vw;height:2px;background:linear-gradient(90deg,transparent,rgba(255,255,255,.4));transform:translateY(-50%)}
@keyframes scan-cross{0%{transform:translateX(0)}50%,100%{transform:translateX(125vw)}}

/* The runway, in perspective, rushing towards you */
.yj-scan .ground{position:absolute;left:0;right:0;top:70.2%;bottom:0;perspective:260px;perspective-origin:50% 0;overflow:hidden}
.yj-scan .strip{position:absolute;left:50%;top:0;width:min(34vw,420px);height:260%;transform:translateX(-50%) rotateX(78deg);transform-origin:50% 0;
  background:
    repeating-linear-gradient(180deg,rgba(255,255,255,.9) 0 18px,transparent 18px 48px) 50% 0/6px 100% no-repeat,
    radial-gradient(circle,#FFE3A3 0 4px,rgba(255,200,110,.35) 5px,transparent 8px) 0 0/18px 24px repeat-y,
    radial-gradient(circle,#FFE3A3 0 4px,rgba(255,200,110,.35) 5px,transparent 8px) 100% 0/18px 24px repeat-y,
    linear-gradient(90deg,transparent,#151B26 6%,#1C2330 50%,#151B26 94%,transparent);
  animation:scan-runway .9s linear infinite}
@keyframes scan-runway{to{background-position:50% 48px,0 24px,100% 24px,0 0}}
.yj-scan .ground::after{content:"";position:absolute;inset:0;background:linear-gradient(180deg,rgba(6,8,13,.85),transparent 45%)}

/* The phone and its boarding pass, being scanned */
.yj-scan .phone{position:relative;width:min(23vh,40vw,230px);aspect-ratio:9/17.5;border-radius:12% / 6%;padding:5%;
  background:linear-gradient(145deg,#2A3346,#121826);box-shadow:0 0 0 2px #3A4560,0 30px 60px -20px #000,0 0 80px -10px rgba(242,194,48,.25);
  animation:scan-float 4s ease-in-out infinite}
@keyframes scan-float{0%,100%{transform:translateY(0) rotate(-4deg)}50%{transform:translateY(-10px) rotate(-2deg)}}
.yj-scan .screen{position:relative;height:100%;border-radius:9% / 4.5%;overflow:hidden;background:${PAPER};color:${INK};display:flex;flex-direction:column}
.yj-scan .screen .band{background:${INK};color:${PAPER};font-size:min(1.05vh,1.8vw);letter-spacing:.2em;font-weight:700;padding:8% 6%;text-align:left}
.yj-scan .screen .route{display:flex;justify-content:space-between;align-items:center;padding:7% 8% 0;font-family:'Oswald',sans-serif;font-size:min(3vh,5vw);font-weight:600}
.yj-scan .screen .qr{margin:auto 14% 12%;aspect-ratio:1}
.yj-scan .laser{position:absolute;left:4%;right:4%;height:3px;border-radius:3px;background:${RED};
  box-shadow:0 0 10px 2px ${RED},0 0 34px 6px rgba(255,90,78,.5);animation:scan-sweep 2.2s ease-in-out infinite}
@keyframes scan-sweep{0%,100%{top:46%}50%{top:90%}}
.yj-scan .ring{position:absolute;inset:-4%;border:2px solid rgba(242,194,48,.55);border-radius:14% / 7%;animation:scan-ring 2.2s ease-out infinite}
.yj-scan .ring.two{animation-delay:1.1s}
@keyframes scan-ring{from{transform:scale(.96) rotate(-4deg);opacity:.9}to{transform:scale(1.14) rotate(-4deg);opacity:0}}
.yj-scan .chevrons span{display:block;width:18px;height:18px;border-right:3px solid ${GOLD};border-bottom:3px solid ${GOLD};transform:rotate(45deg);margin:-6px auto 0;
  animation:scan-chevron 1.5s ease-in-out infinite}
.yj-scan .chevrons span:nth-child(2){animation-delay:.2s}.yj-scan .chevrons span:nth-child(3){animation-delay:.4s}
@keyframes scan-chevron{0%,100%{opacity:.15}40%{opacity:1}}
.yj-scan .pill{display:inline-flex;align-items:center;gap:.6em;border:1.5px solid rgba(242,194,48,.6);color:${GOLD};border-radius:999px;padding:.35em 1em;
  font-weight:700;letter-spacing:.3em}
.yj-scan .pill i{width:.55em;height:.55em;border-radius:50%;background:${GOLD};animation:scan-blink 1.2s ease-in-out infinite}
@keyframes scan-blink{50%{opacity:.2}}

.yj-scan .ticker{white-space:nowrap;overflow:hidden;mask-image:linear-gradient(90deg,transparent,#000 8%,#000 92%,transparent)}
.yj-scan .ticker span{display:inline-block;animation:scan-ticker 45s linear infinite}
@keyframes scan-ticker{from{transform:translateX(0)}to{transform:translateX(-33.333%)}}

/* A moment (welcome, welcome back, unknown pass): wipes over the gate */
.yj-scan .moment{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:4vh 5vw;
  animation:scan-wipe .7s cubic-bezier(.7,0,.2,1) both}
.yj-scan .moment.leaving{animation:scan-fade .5s ease both}
@keyframes scan-wipe{from{clip-path:circle(0% at 50% 60%)}to{clip-path:circle(150% at 50% 60%)}}
@keyframes scan-fade{to{opacity:0}}
.yj-scan .runway{position:absolute;left:0;right:0;bottom:7%;height:3px;
  background:repeating-linear-gradient(90deg,rgba(255,255,255,.75) 0 40px,transparent 40px 80px);animation:scan-runway-line 2.6s ease .2s both}
@keyframes scan-runway-line{0%{opacity:0}15%,75%{opacity:1}100%{opacity:0}}
.yj-scan .landing{position:absolute;bottom:calc(7% + 4px);left:50%;width:min(11vw,120px);margin-left:calc(min(11vw,120px) / -2);
  animation:scan-land 2.2s cubic-bezier(.35,.1,.25,1) .15s both}
@keyframes scan-land{0%{transform:translate(-75vw,-60vh) rotate(62deg);opacity:0}12%{opacity:1}
  62%{transform:translate(0,0) rotate(45deg)}100%{transform:translate(75vw,0) rotate(45deg);opacity:0}}
.yj-scan .rise{display:inline-block;animation:scan-rise .6s cubic-bezier(.2,1.4,.4,1) both}
@keyframes scan-rise{from{transform:translateY(60%) scale(.8);opacity:0}to{transform:none;opacity:1}}
.yj-scan .in{animation:scan-in .6s ease both}
@keyframes scan-in{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}

/* The guest's boarding pass, sliding up to be stamped */
.yj-scan .pass{position:relative;text-shadow:none;width:min(780px,88vw);border-radius:22px;overflow:hidden;background:${PAPER};color:${INK};text-align:left;
  box-shadow:0 40px 80px -30px rgba(0,0,0,.65);animation:scan-pass .8s cubic-bezier(.2,1.2,.3,1) 1.1s both}
@keyframes scan-pass{from{transform:translateY(60vh) rotate(6deg);opacity:0}to{transform:rotate(-1.5deg);opacity:1}}
.yj-scan .pass .band{display:flex;justify-content:space-between;align-items:center;padding:min(1.8vh,16px) min(3vw,32px);color:${PAPER};
  font-weight:700;letter-spacing:.24em;font-size:clamp(11px,min(1.4vw,1.9vh),17px)}
.yj-scan .pass .body{padding:min(2.6vh,26px) min(3vw,32px) min(3vh,28px);display:flex;flex-direction:column;gap:min(2.2vh,20px)}
.yj-scan .pass .label{font-size:clamp(10px,min(1.1vw,1.5vh),14px);font-weight:700;letter-spacing:.22em;color:${MUTED}}
.yj-scan .pass .route{display:grid;grid-template-columns:1fr auto 1fr;align-items:center;gap:min(2vw,24px)}
.yj-scan .pass .code{font-family:'Oswald',sans-serif;font-weight:600;line-height:1;font-size:clamp(40px,min(7vw,10vh),104px);letter-spacing:.02em}
.yj-scan .pass .dash{position:relative;height:2px;width:min(16vw,200px);background:repeating-linear-gradient(90deg,${MUTED} 0 8px,transparent 8px 16px)}
.yj-scan .pass .dash svg{position:absolute;left:50%;top:50%;width:min(3.4vw,40px);transform:translate(-50%,-50%) rotate(45deg);animation:scan-fly 1.4s ease-in-out 1.5s both}
@keyframes scan-fly{from{left:0}to{left:100%}}
.yj-scan .pass .city{font-size:clamp(13px,min(1.6vw,2.2vh),21px);font-weight:600;margin-top:.35em}
.yj-scan .pass .perf{border-top:2px dashed #D9D2C3;margin:0 min(3vw,32px)}
.yj-scan .stamp{position:absolute;left:50%;top:30%;border:6px solid currentColor;white-space:nowrap;border-radius:16px;padding:.1em .5em;font-weight:800;letter-spacing:.16em;
  font-size:clamp(26px,min(4.4vw,6vh),60px);transform:rotate(-12deg);mix-blend-mode:multiply;animation:scan-stamp .5s cubic-bezier(.3,1.6,.5,1) 2.2s both}
.yj-scan .stamp.free{position:static;mix-blend-mode:normal;animation-delay:.2s}
@keyframes scan-stamp{from{transform:translateX(-50%) rotate(-12deg) scale(2.6);opacity:0}to{transform:translateX(-50%) rotate(-12deg) scale(1);opacity:.92}}
.yj-scan .stamp.free{animation-name:scan-stamp-free}
@keyframes scan-stamp-free{from{transform:rotate(-8deg) scale(2.6);opacity:0}to{transform:rotate(-8deg) scale(1);opacity:1}}
.yj-scan .shake{animation:scan-shake .5s ease both}
@keyframes scan-shake{0%,100%{transform:none}20%,60%{transform:translateX(-14px)}40%,80%{transform:translateX(14px)}}

/* Confetti */
.yj-scan .confetti{position:absolute;left:50%;top:45%;width:12px;height:18px;border-radius:2px;opacity:0;
  animation:scan-confetti 2.8s cubic-bezier(.15,.7,.3,1) var(--d) both}
@keyframes scan-confetti{0%{transform:translate(-50%,-50%) scale(.3) rotate(0);opacity:1}
  45%{transform:translate(calc(-50% + var(--x)),calc(-50% + var(--y))) rotate(var(--r));opacity:1}
  100%{transform:translate(calc(-50% + var(--x) * 1.25),calc(-50% + var(--y) + 60vh)) rotate(calc(var(--r) * 2.2));opacity:0}}

.yj-scan .fs{position:absolute;top:14px;right:14px;opacity:.3;transition:opacity .2s}
.yj-scan .fs:hover{opacity:1}
@media (prefers-reduced-motion:reduce){
  .yj-scan *,.yj-scan *::before,.yj-scan *::after{animation-duration:.01ms!important;animation-iteration-count:1!important;animation-delay:0ms!important}
  .yj-scan .confetti,.yj-scan .landing,.yj-scan .sky-plane{display:none}
}
`;

// A fixed, made-up QR pattern for the phone on the idle screen (21 x 21, with the three finder squares)
const FINDERS = [[0, 0], [14, 0], [0, 14]];
const QR_CELLS = (() => {
    const cells = [];
    let seed = 7;
    const next = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
    for (let y = 0; y < 21; y += 1) {
        for (let x = 0; x < 21; x += 1) {
            const finder = FINDERS.find(([fx, fy]) => x >= fx && x < fx + 7 && y >= fy && y < fy + 7);
            if (finder) {
                const dx = x - finder[0];
                const dy = y - finder[1];
                if (dx === 0 || dx === 6 || dy === 0 || dy === 6 || (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4)) cells.push([x, y]);
            } else if (next() > 0.55) {
                cells.push([x, y]);
            }
        }
    }
    return cells;
})();

const STARS = Array.from({ length: 46 }, (_, i) => ({
    left: `${(i * 37 + 11) % 100}%`,
    top: `${(i * 53) % 55}%`,
    t: `${2.5 + (i % 5) * 0.7}s`,
    d: `${(i % 7) * 0.4}s`,
}));

/**
 * The airport chime: three notes up for a welcome, two for a welcome back, two down for a problem. Needs an
 * AudioContext made during a key press (a scan counts).
 */
const chime = (audio, kind) => {
    if (!audio) return;
    const notes = kind === "welcome" ? [523.25, 659.25, 783.99] : kind === "back" ? [659.25, 783.99] : [392, 311.13];
    try {
        notes.forEach((frequency, i) => {
            const oscillator = audio.createOscillator();
            const gain = audio.createGain();
            const start = audio.currentTime + i * 0.22;
            oscillator.type = "sine";
            oscillator.frequency.value = frequency;
            gain.gain.setValueAtTime(0.0001, start);
            gain.gain.exponentialRampToValueAtTime(0.3, start + 0.03);
            gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.9);
            oscillator.connect(gain).connect(audio.destination);
            oscillator.start(start);
            oscillator.stop(start + 0.95);
        });
    } catch {
        // No sound is fine
    }
};

/**
 * A colour a little darker, for the welcome's background: '#C8442F' -> 'rgb(110, 37, 26)'
 */
const darker = (hex, amount = 0.45) => {
    const value = parseInt(hex.slice(1), 16);
    const channel = (shift) => Math.round(((value >> shift) & 255) * (1 - amount));
    return `rgb(${channel(16)}, ${channel(8)}, ${channel(0)})`;
};

const Plane = ({ className, style, fill = PAPER }) => (
    <svg className={className} style={style} viewBox="0 0 24 24" fill={fill} aria-hidden="true"><path d={PLANE_PATH} /></svg>
);

const Qr = ({ className }) => (
    <svg className={className} viewBox="0 0 21 21" shapeRendering="crispEdges" aria-hidden="true">
        {QR_CELLS.map(([x, y]) => <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill={INK} />)}
    </svg>
);

/**
 * Letters that rise in one after another
 */
const RisingText = ({ text, delay = 0, step = 45 }) => {
    let index = 0;
    return (
        <span aria-label={text}>
            {text.split(" ").map((word, w) => (
                <React.Fragment key={w}>
                    {w > 0 && " "}
                    {/* A word never breaks across lines */}
                    <span className="inline-block whitespace-nowrap" aria-hidden="true">
                        {[...word].map((char) => {
                            index += 1;
                            return <span key={index} className="rise" style={{ animationDelay: `${delay + index * step}ms` }}>{char}</span>;
                        })}
                    </span>
                </React.Fragment>
            ))}
        </span>
    );
};

/**
 * Confetti in the guest's colour, gold and white, from the middle of the screen
 */
const Confetti = ({ colour }) => {
    const pieces = useMemo(() => Array.from({ length: CONFETTI_PIECES }, (_, i) => {
        const angle = (Math.PI * 2 * i) / CONFETTI_PIECES + Math.random() * 0.4;
        const distance = 22 + Math.random() * 30;
        return {
            x: `${Math.cos(angle) * distance}vw`,
            y: `${Math.sin(angle) * distance * 0.8 - 12}vh`,
            r: `${Math.round(Math.random() * 720 - 360)}deg`,
            d: `${2300 + Math.random() * 300}ms`,
            colour: [colour, GOLD, PAPER, "#7FC8F8"][i % 4],
            round: i % 3 === 0,
        };
    }), [colour]);
    return pieces.map((piece, i) => (
        <span key={i} className="confetti" aria-hidden="true"
            style={{ "--x": piece.x, "--y": piece.y, "--r": piece.r, "--d": piece.d, background: piece.colour, borderRadius: piece.round ? "50%" : 2 }} />
    ));
};

/**
 * Waiting for the next guest: the gate at dusk, and a phone being scanned
 */
const Gate = ({ shaking }) => (
    <div className="absolute inset-0 flex flex-col">
        <div className="flex-1 min-h-0 flex flex-col min-[900px]:flex-row items-center justify-center gap-[5vh] min-[900px]:gap-[7vw] px-[6vw] pb-[24vh] pt-[6vh]">
            <div className="flex flex-col items-center min-[900px]:items-start text-center min-[900px]:text-left gap-[2.4vh] max-w-[640px]">
                <span className="pill text-[clamp(11px,min(1.2vw,1.7vh),15px)]"><i />ARRIVALS · GATE BAH</span>
                <h1 className="flap uppercase font-semibold leading-[.95] tracking-[0.02em] text-[clamp(40px,min(7.4vw,10vh),118px)]">
                    Welcome to<br /><span style={{ color: GOLD }}>Bahubali Enclave</span>
                </h1>
                <p className="text-[clamp(16px,min(2vw,2.8vh),28px)] opacity-90">Scan your boarding pass to land.</p>
                <p className="text-[clamp(13px,min(1.4vw,2vh),19px)] opacity-60">Open it on your phone and hold the QR code under the scanner.</p>
            </div>
            <div className="flex flex-col items-center gap-[2.4vh] shrink-0">
                <div className={`relative ${shaking ? "shake" : ""}`}>
                    <span className="ring" /><span className="ring two" />
                    <div className="phone">
                        <div className="screen">
                            <div className="band">RIGHT BROTHER AIRWAYS</div>
                            <div className="route"><span>YJ</span><Plane fill={INK} style={{ width: "26%", transform: "rotate(45deg)" }} /><span>BAH</span></div>
                            <Qr className="qr" />
                            <span className="laser" />
                        </div>
                    </div>
                </div>
                <div className="chevrons" aria-hidden="true"><span /><span /><span /></div>
            </div>
        </div>
    </div>
);

/**
 * The guest's boarding pass, in their colour: from their city to Bahubali Enclave, stamped
 */
const ArrivalPass = ({ guest, stamp, stampColour }) => (
    <div className="pass">
        <div className="band" style={{ background: darker(guest.colour, 0.15) }}>
            <span>RIGHT BROTHER AIRWAYS</span>
            <span className="mono">YJ030 · 30 OCT</span>
        </div>
        <div className="body">
            <div>
                <p className="label">PASSENGER</p>
                <p className="font-bold text-[clamp(20px,min(2.6vw,3.6vh),36px)] leading-tight">{guest.fullName}</p>
            </div>
            <div className="route">
                <div>
                    <p className="code">{guest.fromCode}</p>
                    <p className="city">{[guest.fromCity, guest.country].filter(Boolean).join(", ")}</p>
                </div>
                <div className="dash"><Plane fill={guest.colour} /></div>
                <div className="text-right">
                    <p className="code">BAH</p>
                    <p className="city">Bahubali Enclave</p>
                </div>
            </div>
        </div>
        <div className="perf" />
        <div className="body" style={{ flexDirection: "row", justifyContent: "space-between", paddingTop: "min(1.6vh,14px)" }}>
            <div><p className="label">LANDED</p><p className="mono font-bold text-[clamp(16px,min(2vw,2.8vh),26px)]">{formatTime(guest.arrivedAt)}</p></div>
            <div><p className="label">GATE</p><p className="mono font-bold text-[clamp(16px,min(2vw,2.8vh),26px)]">PARTY</p></div>
            <div className="text-right"><p className="label">STATUS</p><p className="mono font-bold text-[clamp(16px,min(2vw,2.8vh),26px)]" style={{ color: "#0D6B4F" }}>ARRIVED</p></div>
        </div>
        <span className="stamp" style={{ color: stampColour }}>{stamp}</span>
    </div>
);

/**
 * A guest just landed (or landed again, or their pass wasn't found)
 * @param {Object} moment   -   { kind: 'welcome' | 'back' | 'unknown' | 'error', guest, leaving }
 */
const Moment = ({ moment }) => {
    const { kind, guest, leaving } = moment;
    const landed = kind === "welcome" || kind === "back";
    const colour = guest?.colour || INK;
    const background = landed
        ? `radial-gradient(120% 100% at 50% 30%, ${colour} 0%, ${darker(colour)} 100%)`
        : `radial-gradient(120% 100% at 50% 40%, #3A1F2B 0%, ${NIGHT} 100%)`;
    const firstName = guest?.fullName.split(" ")[0];
    return (
        <div className={`moment ${leaving ? "leaving" : ""}`} style={{ background }} role="status" aria-live="assertive">
            {/* A dark veil, so white text reads on any guest colour */}
            <div className="absolute inset-0" style={{ background: "linear-gradient(rgba(5,10,20,.12), rgba(5,10,20,.45))" }} aria-hidden="true" />
            {kind === "welcome" && (
                <>
                    <span className="runway" aria-hidden="true" />
                    <Plane className="landing" />
                    <Confetti colour={colour} />
                </>
            )}
            <div className="relative flex flex-col items-center gap-[3.2vh] text-center" style={{ textShadow: "0 3px 18px rgba(0,0,0,.3)" }}>
                {kind === "welcome" && (
                    <>
                        <p className="flap uppercase font-semibold leading-none tracking-[0.02em] text-[clamp(52px,min(9vw,13vh),150px)]">
                            <RisingText text={`Welcome, ${firstName}!`} delay={350} />
                        </p>
                        <ArrivalPass guest={guest} stamp="LANDED" stampColour="#C8442F" />
                    </>
                )}
                {kind === "back" && (
                    <>
                        <p className="in flap uppercase font-semibold leading-none text-[clamp(44px,min(7.5vw,11vh),128px)]" style={{ animationDelay: "250ms" }}>
                            Welcome back, {firstName}!
                        </p>
                        <p className="in text-[clamp(16px,min(2vw,2.8vh),28px)] opacity-90" style={{ animationDelay: "500ms" }}>You're already through. Go on in.</p>
                        <ArrivalPass guest={guest} stamp="ALREADY LANDED" stampColour={INK} />
                    </>
                )}
                {kind === "unknown" && (
                    <>
                        <span className="stamp free" style={{ color: RED }}>NOT ON THE MANIFEST</span>
                        <p className="in flap uppercase font-semibold leading-none text-[clamp(40px,min(6.5vw,9vh),110px)]" style={{ animationDelay: "250ms" }}>We couldn't find that pass</p>
                        <p className="in text-[clamp(16px,min(2vw,2.8vh),28px)] opacity-85" style={{ animationDelay: "450ms" }}>Try scanning again, or ask a member of the crew.</p>
                    </>
                )}
                {kind === "error" && (
                    <>
                        <p className="in flap uppercase font-semibold leading-none text-[clamp(40px,min(6.5vw,9vh),110px)]">Lost contact with the tower</p>
                        <p className="in text-[clamp(16px,min(2vw,2.8vh),28px)] opacity-85" style={{ animationDelay: "200ms" }}>Please scan your pass again.</p>
                    </>
                )}
            </div>
        </div>
    );
};

/**
 * The scanning screen itself, once the staff key is known
 */
const Kiosk = ({ staffKey, onBadKey }) => {
    const [moment, setMoment] = useState(null);
    const [shaking, setShaking] = useState(false);
    const [clock, setClock] = useState(() => formatTime(new Date().toISOString()));
    const audioRef = useRef(null);
    const scansRef = useRef(Promise.resolve());
    const lastCodeRef = useRef({ code: "", at: 0 });
    const shownAtRef = useRef(0);
    const timersRef = useRef([]);

    useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

    useEffect(() => {
        const tick = setInterval(() => setClock(formatTime(new Date().toISOString())), 10000);
        return () => clearInterval(tick);
    }, []);

    const show = useCallback((next, ms) => {
        timersRef.current.forEach(clearTimeout);
        shownAtRef.current = Date.now();
        setMoment({ ...next, id: Date.now() });
        chime(audioRef.current, next.kind);
        timersRef.current = [
            setTimeout(() => setMoment((current) => current && { ...current, leaving: true }), ms - 500),
            setTimeout(() => setMoment(null), ms),
        ];
    }, []);

    const handle = useCallback(async (code) => {
        let next;
        let ms = NOTICE_MS;
        try {
            const response = await markArrived(staffKey, code);
            next = { kind: response.already ? "back" : "welcome", guest: response.guest };
            ms = response.already ? NOTICE_MS + 1000 : WELCOME_MS;
        } catch (err) {
            if (err.status === 401) {
                onBadKey();
                return;
            }
            next = { kind: err.status === 404 ? "unknown" : "error" };
        }
        // Guests scanning one after another: each welcome is up for a few seconds before the next
        const wait = MIN_WELCOME_MS - (Date.now() - shownAtRef.current);
        if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
        if (next.kind === "unknown" || next.kind === "error") {
            setShaking(true);
            setTimeout(() => setShaking(false), 600);
        }
        show(next, ms);
    }, [staffKey, onBadKey, show]);

    useBarcodeScanner((code) => {
        const now = Date.now();
        // The same pass twice in a row (a double read) counts once
        if (code === lastCodeRef.current.code && now - lastCodeRef.current.at < SAME_CODE_MS) return;
        lastCodeRef.current = { code, at: now };
        try {
            audioRef.current = audioRef.current || new (window.AudioContext || window.webkitAudioContext)();
            audioRef.current.resume();
        } catch {
            // No sound is fine
        }
        scansRef.current = scansRef.current.then(() => handle(code));
    });

    const fullScreen = () => {
        try {
            document.documentElement.requestFullscreen?.().catch(() => {});
        } catch {
            // Not available here
        }
    };

    return (
        <div className="yj-scan">
            <style>{STYLES}</style>
            {STARS.map((star, i) => <span key={i} className="star" style={{ left: star.left, top: star.top, "--t": star.t, "--d": star.d }} aria-hidden="true" />)}
            <div className="glow" aria-hidden="true" />
            <Plane className="sky-plane" style={{ transform: "rotate(45deg)" }} />
            <div className="ground" aria-hidden="true"><div className="strip" /></div>

            <header className="absolute top-0 left-0 right-0 flex items-center justify-between px-[4vw] pt-[3vh] text-[clamp(11px,min(1.2vw,1.7vh),16px)] font-bold tracking-[0.28em] opacity-80">
                <span>RIGHT BROTHER AIRWAYS · YJ030</span>
                <span className="mono tracking-[0.1em]">LOCAL TIME {clock}</span>
            </header>
            <Gate shaking={shaking} />
            <footer className="absolute bottom-[3vh] left-0 right-0 px-[4vw]">
                <div className="ticker flap text-[clamp(13px,min(1.4vw,2vh),20px)] tracking-[0.18em] opacity-75"><span>{TICKER.repeat(3)}</span></div>
            </footer>

            {moment && <Moment key={moment.id} moment={moment} />}

            <button type="button" className="fs" onClick={fullScreen} aria-label="Full screen"><Maximize size={20} color={PAPER} /></button>
        </div>
    );
};

const ScanPassesPage = () => {
    const [key, saveKey, forgetKey] = useStoredKey("yj030-staff-key");
    const [keyError, setKeyError] = useState("");
    const [checked, setChecked] = useState(false);

    useNoIndex("Welcome · Flight YJ030");
    useWakeLock();

    // A link with ?key=… sets this laptop up in one go: the key is saved here and taken out of the address bar
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const fromLink = params.get("key");
        if (fromLink) {
            saveKey(fromLink);
            window.history.replaceState(null, "", window.location.pathname);
        }
        // Only on arrival
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Checks the key once, so a wrong one is caught before the first guest scans
    useEffect(() => {
        if (!key) return;
        setChecked(false);
        getCounterGuests(key)
            .then(() => { setKeyError(""); setChecked(true); })
            .catch((err) => {
                if (err.status === 401) {
                    forgetKey();
                    setKeyError("That key didn't work.");
                } else {
                    setChecked(true);
                }
            });
    }, [key, forgetKey]);

    const badKey = useCallback(() => {
        forgetKey();
        setKeyError("That key stopped working. Enter it again.");
    }, [forgetKey]);

    if (!key || !checked) {
        return (
            <div className="min-h-[100dvh] px-4 py-6" style={{ background: NIGHT }}>
                {!key && <KeyGate title="Arrivals scanner" label="Staff key" error={keyError} onSubmit={(value) => { setKeyError(""); saveKey(value); }} />}
            </div>
        );
    }
    return <Kiosk staffKey={key} onBadKey={badKey} />;
};

export default ScanPassesPage;
